import { z } from "zod";
import {
    durationInMinutesZodSchema,
    type DurationInMinutes,
} from "../date-time/time-spans";
import {
    orderConfirmationOption,
    orderConfirmationOptionZodSchema,
    type OrderConfirmationOption,
} from "./order-confirmation-options";
import {
    bookableSessionAvailabilityZodSchema,
    type BookableSessionAvailability,
} from "./sessions/bookable-session-availability";

type BookableSettings_V2 = {
    _type?: "BOOKABLE_SETTINGS";
    allowBookingDuringSession: boolean;

    /** `prepTime` was the time before the session starts the bookable cannot be booked
     *
     * @deprecated replaced by advance Notice
     */
    prepTime?: DurationInMinutes;

    /** `postTime` is the time after the session ends the bookable resources cannot be booked */
    postTime: DurationInMinutes;

    orderConfirm: OrderConfirmationOption;
    sessionAvailability?: BookableSessionAvailability;

    /** `advanceNotice` is the time before the session starts the bookable cannot be booked */
    advanceNotice?: DurationInMinutes;

    /**
     * `preparationTime` is the time before the session starts the bookable-resources
     * cannot be booked similar to `postTime`
     */
    preparationTime?: DurationInMinutes;
};

type AccommodationSettings = {
    _type: "ACCOMMODATION_SETTINGS";
    allowBookingDuringSession: boolean;
    advanceNotice: DurationInMinutes;
    preparationTime: DurationInMinutes;
    postTime: DurationInMinutes;
    orderConfirm: OrderConfirmationOption;
};

type BookableSettings = BookableSettings_V2 | AccommodationSettings;

const convertLegacyBookableSettings = (
    settings: BookableSettings,
): BookableSettings => {
    if (settings._type === "ACCOMMODATION_SETTINGS") return settings;

    if (
        settings.prepTime === undefined ||
        settings.preparationTime !== undefined
    )
        return settings;

    return {
        _type: "BOOKABLE_SETTINGS",
        allowBookingDuringSession: settings.allowBookingDuringSession,
        advanceNotice: settings.prepTime,
        prepTime: undefined,
        preparationTime: 0 as DurationInMinutes,
        postTime: settings.postTime,
        orderConfirm: settings.orderConfirm,
        sessionAvailability: settings.sessionAvailability,
    };
};

const defaultBookableSettings: BookableSettings_V2 = {
    _type: "BOOKABLE_SETTINGS",
    allowBookingDuringSession: false,
    advanceNotice: 0 as DurationInMinutes,
    postTime: 0 as DurationInMinutes,
    preparationTime: 0 as DurationInMinutes,
    prepTime: undefined,
    orderConfirm: orderConfirmationOption.AUTO,
    sessionAvailability: undefined,
};

const defaultAccommodationSettings: AccommodationSettings = {
    _type: "ACCOMMODATION_SETTINGS",
    allowBookingDuringSession: false,
    advanceNotice: 0 as DurationInMinutes,
    preparationTime: 0 as DurationInMinutes,
    postTime: 0 as DurationInMinutes,
    orderConfirm: orderConfirmationOption.AUTO,
};

const bookableSettingsV2ZodSchema: z.ZodSchema<BookableSettings_V2> = z.object({
    _type: z.literal("BOOKABLE_SETTINGS").optional(),
    allowBookingDuringSession: z.boolean(),
    advanceNotice: durationInMinutesZodSchema.optional(),
    prepTime: durationInMinutesZodSchema.optional(),
    postTime: durationInMinutesZodSchema,
    preparationTime: durationInMinutesZodSchema.optional(),
    orderConfirm: orderConfirmationOptionZodSchema,
    sessionAvailability: bookableSessionAvailabilityZodSchema.optional(),
});

const accommodationSettingsZodSchema: z.ZodSchema<AccommodationSettings> =
    z.object({
        _type: z.literal("ACCOMMODATION_SETTINGS"),
        allowBookingDuringSession: z.boolean(),
        advanceNotice: durationInMinutesZodSchema,
        preparationTime: durationInMinutesZodSchema,
        postTime: durationInMinutesZodSchema,
        orderConfirm: orderConfirmationOptionZodSchema,
    });

const bookableSettingsZodSchema: z.ZodSchema<BookableSettings> = z.union([
    bookableSettingsV2ZodSchema,
    accommodationSettingsZodSchema,
]);

export { accommodationSettingsZodSchema };

export {
    bookableSettingsV2ZodSchema,
    bookableSettingsZodSchema,
    convertLegacyBookableSettings,
    defaultAccommodationSettings,
    defaultBookableSettings,
};
export type { AccommodationSettings, BookableSettings, BookableSettings_V2 };
